import * as ko from "knockout";
import {hasher} from 'hasher';
import {_} from 'lodash';
import pubsub from "../../pubsub";
import {DEFAULT_SEARCH_PAGE, DEFAULT_SORT_ORDER, REQUEST_THROTTLE, REQUEST_THROTTLE_QUICK} from "../../constants";
import {
    IMessageData,
    IResponse,
    IRouteDefinitionParams,
    ISearchParams,
    ITableColumn,
    TBaseOrObservableOrFunctionType
} from "../../index";
import {KOSubscriptionTracker} from "../../util/KOSubscriptionTracker";
import {PromiseRequestThrottle} from "../../req/PromiseRequestThrottle";
import {global, lm} from "../../appContext";
import {memoryStore} from "../../util/memorystore";
import {customStoreObservable, storeObservable} from "../../util/sessionstore";
import {getChildHash, setRouteHash} from "../../constants/RouteDefinitions";
import {nsRoute} from "../../util/route";
import {createErrorMessage, getSearchMarkup} from "../../util/misc";
import {ISearchPageButton, ISearchRowButtons} from "./index";
import {nsSearch} from "../../constants/Language";
import {nsSearchToggle} from "./nsSearchToggle";
import {ISelectedListActions, ISelectedListParams} from "../object/selected-list";
import {nsLanguage} from "../../util/nsLanguage";
import {ISplitButtonParams} from "@component/button/splitButton/split-button";
import {getSearchBoxHeadingKey} from "./boxHeadingKey";
import ParsedRouteDataDecorator = nsRoute.ParsedRouteDataDecorator;
import ItemCheckbox = nsSearchToggle.ItemCheckbox;
import AllCheckbox = nsSearchToggle.AllCheckbox;
import searchHtml from './search.html?raw';
import search_rowHtml from './search-row.html?raw';

export const tpl: string = searchHtml.replace(/<!-- TEMPLATE_ROW -->/gm, search_rowHtml);
export const template: string = tpl;

interface IAttributes {
    css: string;
    title: string;
}

export interface ICell<T extends any = any> {
    item: T
    key: keyof T
    val?: T[keyof T]
    mark?: boolean
}

export interface IMultiOperation {
    href?: string
    icon: string
    label: string
    fn?: () => void
}

export abstract class viewModel<T = any> extends ParsedRouteDataDecorator {
    protected readonly _searchMessage: KnockoutObservable<IMessageData | null> = ko.observable(null);
    protected readonly abstract TABLE: ITableColumn<ICell['item']>[];
    protected readonly kosub: KOSubscriptionTracker = new KOSubscriptionTracker();
    protected readonly reqSearch: PromiseRequestThrottle;

    protected readonly isError: KnockoutObservable<boolean> = ko.observable(false);
    protected readonly isWaiting: KnockoutObservable<boolean>;
    protected readonly isDeleting: KnockoutObservable<boolean> = ko.observable(false);

    protected readonly hasFocus: KnockoutObservable<boolean> = ko.observable(false);
    protected readonly fnRow: KnockoutObservableArray<ISearchRowButtons> | ISearchRowButtons[] = ko.observableArray<ISearchRowButtons>([]);
    protected readonly fnPage: ISearchPageButton[] | KnockoutObservableArray<ISearchPageButton> = [];

    protected data: IResponse<ICell['item']> | null = null;
    protected dataArray: KnockoutObservableArray<ICell['item']> = ko.observableArray<ICell['item']>([]);

    protected readonly asc: KnockoutObservable<boolean>;
    protected readonly maxpagenr!: TBaseOrObservableOrFunctionType<number>;
    protected readonly pagenr: KnockoutObservable<number>;
    protected readonly pages: KnockoutObservable<number | null> = ko.observable(null);
    protected readonly resultsprpage = storeObservable("num.search.results");
    protected readonly sortcol: KnockoutObservable<string>;
    protected readonly totalresults: KnockoutObservable<number> = ko.observable(0);
    protected readonly searchkey: KnockoutObservable<string>;
    protected readonly showAll: KnockoutObservable<boolean>;
    private sortableColumns: string[] = [];
    protected readonly selectedObjects: KnockoutObservableArray<T> = ko.observableArray<T>([])
    protected checkedKey: keyof T = 'id' as keyof T;
    protected getValue: ISelectedListParams<any>["getValue"] | undefined = undefined

    protected constructor(params: IRouteDefinitionParams) {
        super(params);

        this.reqSearch = new PromiseRequestThrottle(this.__routeDef?.serviceName ?? 'searchobject')
        this.isWaiting = this.reqSearch.isRequesting

        this.asc = customStoreObservable(
            this.__routeId + '.asc',
            this.__routeDef?.sortAscending ?? DEFAULT_SORT_ORDER,
        );
        this.pagenr = memoryStore.get(this.__routeId + '.pagenr', DEFAULT_SEARCH_PAGE);
        this.searchkey = memoryStore.get(this.__routeId + '.searchkey', "");
        this.showAll = customStoreObservable(this.__routeId + '.show.all', false);
        this.sortcol = customStoreObservable(this.__routeId + '.sortcol', this.__routeDef?.sortColumn ?? 'Name');

        this.kosub.subscribe(global.login.selectedOrganization, () => this.preSubmit(true, true, 0));
        this.kosub.subscribe(this.asc, () => this.preSubmit(true, false));
        this.kosub.subscribe(this.resultsprpage, () => this.preSubmit(true, false));
        this.kosub.subscribe(this.pagenr, () => this.preSubmit(false, false));
        this.kosub.subscribe(this.searchkey, () => this.preSubmit(true, true, REQUEST_THROTTLE));
        this.kosub.subscribe(this.sortcol, () => this.preSubmit(true, false));
        this.kosub.subscribe(this.showAll, (bool: boolean) => {
            setTimeout(() => this.hasFocus(!bool), 1);
            this.preSubmit();
        });

        global.resetObservableOnOrganizationChange(this.searchkey, '');
        global.resetObservableOnOrganizationChange(this.pagenr, DEFAULT_SEARCH_PAGE);
        global.resetTrgObsWhenSrcObsChange(this.resultsprpage, this.pagenr, 0);

        pubsub.on(this, "search.needs.refresh", () => this.preSubmit(true, true));
        pubsub.on(this, "search.remove.selection", () => this.selectedObjects([]));

        // Let's await for potential inherited constructor to complete before running a submit.
        requestAnimationFrame(() => {
            this.onSubmit(0)
        })
    }

    /*******************************************************************************************************************
     * Request methods
     *******************************************************************************************************************/

    protected preSubmit(resetPagenr: boolean = true, resetPages: boolean = true, throttleDuration: number = REQUEST_THROTTLE_QUICK) {
        if (this._searchMessage()?.type == 'danger') {
            this._searchMessage(null)
        }
        if (resetPagenr) {
            this.pagenr(DEFAULT_SEARCH_PAGE);
        }
        if (resetPages) {
            this.pages(null);
        }
        this.onSubmit(throttleDuration);
    }

    protected onSubmit(throttleDuration: number = REQUEST_THROTTLE) {
        if (!global.login.selectedOrganization() || !this.hasSearchKey()) {
            this.resetSearch();
            return;
        }
        this.reqSearch.request(this.getParams(), true, undefined, undefined, throttleDuration)
            .then((resp: IResponse<ICell['item']>) => this.onSearchSuccess(resp))
            .catch((resp: IResponse) => {
                const reason = String(resp?.lasterror ?? resp)
                this._searchMessage(createErrorMessage(String(reason)))
                this.resetSearch()
            })
    }

    /**
     * Generates default parameters based on current observable values.
     * @returns {ISearchParams}
     */
    protected getParams(): ISearchParams {
        return {
            asc: this.asc(),
            pagenr: this.pagenr() || DEFAULT_SEARCH_PAGE,
            resultsprpage: this.resultsprpage(),
            searchkey: this.getRealSearchKey(),
            sortcol: this.sortcol(),
        };
    }

    protected getRealSearchKey(): string {
        return this.showAll() ? "" : this.searchkey();
    }

    protected hasSearchKey(): boolean {
        return this.showAll() || !!this.searchkey();
    }

    /** White-box title key (`*.search`); distinct from route `label` used for tabs / page H1. */
    protected readonly searchBoxHeading = ko.pureComputed(() => getSearchBoxHeadingKey(this.__routeDef));

    /** Heading badge: current hit count when a search is active; hidden while idle. */
    protected readonly searchBadge = ko.pureComputed((): number | null => {
        if (!this.hasSearchKey() || this.isWaiting()) {
            return null;
        }
        return this.totalresults();
    });

    /** Heading spinner while an active search request is in flight. */
    protected readonly searchBadgeWaiting = ko.pureComputed((): boolean => {
        return this.hasSearchKey() && this.isWaiting();
    });

    protected resetSearch() {
        this.reqSearch.abort();
        this.data = null;
        this.dataArray([]);
        this.pagenr(0);
        this.pages(null);
        this.totalresults(0);
    }

    protected onSearchSuccess(response: IResponse<ICell['item']>) {
        this.data = response;
        this.sortableColumns = (this.data.sortablecolumns ?? []).map((s: string) => s.toLowerCase());
        this.dataArray((response.data || []) as ICell['item'][]);
        this.pagenr(response.pagenr);
        this.pages(response.pages);
        this.sortcol(response.sortedby);
        this.asc(response.sortascending);
        this.totalresults(response.totalresults);
    }

    /*******************************************************************************************************************
     * Utility methods
     *******************************************************************************************************************/

    protected isSortableColumn(item: ITableColumn<ICell['item']>): boolean {
        return item.sort || this.sortableColumns
            .map((s: string) => s.toLowerCase())
            .includes(String(item.key).toLowerCase())
    }

    protected isSearchableColumn(key: ICell['key']): boolean {
        return this.data?.searchablecolumns?.map((s) => String(s).toLowerCase())
            .includes(String(key).toLowerCase())
        ?? false
    }

    protected getCellCss(item: ITableColumn<ICell['item']>) {
        let css = ["cell", "cell__" + String(item.key)];
        if (this.isSortableColumn(item)) {
            css.push("cell--sortable");
        }
        if (item.count || item.center) {
            css.push("text-center");
        }
        if ('css' in item && item.css) {
            css.push(item.css)
        }
        return css.join(" ");
    }

    protected getCellContent(...objects: Partial<ICell>[]): string | number {
        const obj = _.defaultsDeep.apply(null, objects)
        const mark = obj.mark ?? !this.showAll();
        const str = this.getCellText(obj);

        if (mark && this.isSearchableColumn(obj.key) && this.searchkey()) {
            return getSearchMarkup(str, this.searchkey())
        }

        return str;
    }

    protected getCellText(obj: ICell): string {
        const val = this.getCellVal(obj)
        if (_.isArray(val)) {
            return String(val.length);
        }
        return val;
    }

    protected getCellTitle(obj: ICell): string {
        if (this.selectedObjects().length) {
            return this.getItemCheckbox(obj.item).isChecked()
                ? lm.t('remove') ?? ''
                : lm.t('add') ?? ''
        }
        return this.getCellText(obj)
    }

    protected getCellVal(obj: ICell): any {
        return 'val' in obj
            ? obj.val
            : obj.item[String(obj.key).toLowerCase()]
    }

    /*******************************************************************************************************************
     * Table interaction
     *******************************************************************************************************************/

    protected canRowClick(item: any): boolean {
        return true;
    }

    protected onRowClick(item: any, e: JQuery.Event) {
        if (e.metaKey || e.ctrlKey) {
            const hash = getChildHash(this.__routeDef!, {id: item.id});
            window.open(hash, '_blank');
        } else if (this.__routeDef?.child) {
            setRouteHash(this.__routeDef.child, {id: item.id});
        }
    }

    protected onRowKeyUp(item: any, event: KeyboardEvent) {
        if (event.key == 'Enter') {
            const hash = getChildHash(this.__routeDef!, {id: item.id});
            hasher.setHash(hash);
        }
    };

    /*******************************************************************************************************************
     * Miscellaneous
     *******************************************************************************************************************/

    protected searchMessage = ko.pureComputed((): IMessageData | null => {
        if (this._searchMessage()) {
            return this._searchMessage()
        }
        if (this.dataArray().length) {
            return null
        }
        if (this.hasSearchKey() && !this.isWaiting() && this.totalresults() == 0) {
            return {message: nsSearch.MessageNoResult}
        }
        if (this.hasSearchKey() || this.isWaiting()) {
            return {message: nsSearch.MessageLoading};
        }
        return {message: nsSearch.MessageInitial};
    });

    private getAttr(item: any, data: any): any {
        let attr: any;
        if (_.isFunction(data.attr)) {
            attr = data.attr(item);
        } else {
            attr = data.attr ?? {};
        }
        if (_.isFunction(data.href)) {
            attr.href = data.href(item);
        } else {
            attr.href = data.href || null;
        }
        return attr;
    }

    private isSelector(): boolean {
        return !!this.TABLE.find(o => o.key == 'selector')
    }

    private getAllCheckbox(): AllCheckbox<T> {
        return new AllCheckbox({
            data: this.dataArray,
            key: this.checkedKey,
            selected: this.selectedObjects,
        })
    }

    protected getItemCheckbox(item: T): ItemCheckbox<T> {
        return new ItemCheckbox({
            item: item,
            key: this.checkedKey,
            selected: this.selectedObjects,
        })
    }

    protected selectedObjectsSorted(): T[] {
        return _.sortBy(this.selectedObjects(), this.sortcol())
    }

    protected getSelectedListParams(): ISelectedListParams<T> {
        const actions = this.getSelectedListActions()
        return this.__getParams({
            checkedKey: this.checkedKey,
            getValue: this.getValue?.bind(this),
            selectedObjects: this.selectedObjects,
            selectedObjectsSorted: this.selectedObjects,
            table: this.TABLE,
            quickActions: actions?.quickActions,
            moreActions: actions?.moreActions,
        })
    }

    protected showOperationsButton(): boolean {
        return this.isSelector() && !!this.numAffectedObjects()
    }

    protected multiOperations(): IMultiOperation[] | null {
        return null
    }

    protected numAffectedObjectsText(): string {
        if (this.__objectTypeId == null) {
            return '';
        }
        return nsLanguage.getNumObjectsLabel(this.numAffectedObjects(), this.__objectTypeId, false)
    }

    protected numAffectedObjects(): number {
        return this.selectedObjects().length || this.totalresults()
    }

    protected getContentType(col: ITableColumn, obj: T ) {
        if (col.key == 'selector') {
            return 'selector'
        }
        if (col.key == 'func') {
            return 'func'
        }
        if (col.component) {
            return 'component'
        }
        if (col.getComponent) {
            return 'getComponent'
        }
        if (col.html !== false) {
            if (col.binding === true) {
                return 'htmlWithBinding'
            } else {
                return 'html'
            }
        }
        return 'default';
    }

    protected getSearchSplitButtonParams(): ISplitButtonParams | null {
        return null
    }

    /** Main module creation belongs beside the page title; contextual actions stay in the card. */
    protected getSearchHeaderSplitButtonParams(): ISplitButtonParams | null {
        return null
    }

    protected readonly searchHeaderSplitButtonParams = ko.pureComputed(
        () => this.getSearchHeaderSplitButtonParams(),
    );

    protected getSelectedListActions(): ISelectedListActions | null {
        return null
    }

    override dispose() {
        pubsub.off(this);
        this.kosub.unsuscribeAll();
        this.searchHeaderSplitButtonParams.dispose();
        this.reqSearch.abort();
    }
}
